import { useEffect, useState, type FormEvent } from "react";
import {
  fetchFieldSettings,
  updateFieldSettings,
  type FieldSetting,
} from "@/api/admin";
import { apiErrorMessage } from "@/api/client";

export function AdminSettingsPage() {
  const [fields, setFields] = useState<FieldSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchFieldSettings();
        if (!cancelled)
          setFields([...res].sort((a, b) => a.display_order - b.display_order));
      } catch (e) {
        if (!cancelled) setError(apiErrorMessage(e, "項目設定の取得に失敗しました"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateField = (idx: number, patch: Partial<FieldSetting>) => {
    setFields((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };

  const moveUp = (idx: number) => {
    if (idx <= 0) return;
    setFields((prev) => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next.map((f, i) => ({ ...f, display_order: i + 1 }));
    });
  };
  const moveDown = (idx: number) => {
    if (idx >= fields.length - 1) return;
    setFields((prev) => {
      const next = [...prev];
      [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
      return next.map((f, i) => ({ ...f, display_order: i + 1 }));
    });
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // 全フィールドのラベル必須チェック
    for (const f of fields) {
      if (!f.display_label.trim()) {
        setFlash({ type: "err", text: `「${f.field_key}」の表示ラベルを入力してください` });
        return;
      }
    }
    setSaving(true);
    setFlash(null);
    try {
      const res = await updateFieldSettings(
        fields.map((f, i) => ({ ...f, display_order: i + 1 })),
      );
      setFields([...res].sort((a, b) => a.display_order - b.display_order));
      setFlash({ type: "ok", text: "項目設定を保存しました" });
    } catch (e) {
      setFlash({ type: "err", text: apiErrorMessage(e, "保存に失敗しました") });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-500">読み込み中...</div>;
  if (error)
    return (
      <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
        {error}
      </div>
    );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">項目表示設定</h1>
        <p className="text-sm text-gray-500 mt-1">
          顧客のカタログ画面に表示する項目・表示ラベル・並び順を設定します。
        </p>
      </div>

      {flash && (
        <div
          className={`rounded-md px-4 py-3 text-sm border ${
            flash.type === "ok"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {flash.text}
        </div>
      )}

      <form onSubmit={onSubmit} className="card overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-3 py-2 text-left font-medium">キー</th>
              <th className="px-3 py-2 text-left font-medium">表示ラベル</th>
              <th className="px-3 py-2 text-center font-medium">顧客表示</th>
              <th className="px-3 py-2 text-center font-medium">並び替え</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {fields.map((f, idx) => (
              <tr key={f.field_key}>
                <td className="px-3 py-2 font-mono text-xs text-gray-600">{f.field_key}</td>
                <td className="px-3 py-2">
                  <input
                    className="input"
                    value={f.display_label}
                    onChange={(e) => updateField(idx, { display_label: e.target.value })}
                    required
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={f.is_visible}
                      onChange={(e) => updateField(idx, { is_visible: e.target.checked })}
                    />
                    <span className="text-xs">
                      {f.is_visible ? "表示" : "非表示"}
                    </span>
                  </label>
                </td>
                <td className="px-3 py-2 text-center">
                  <div className="flex justify-center gap-1">
                    <button
                      type="button"
                      className="btn-secondary px-2 py-1 text-xs"
                      onClick={() => moveUp(idx)}
                      disabled={idx === 0}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="btn-secondary px-2 py-1 text-xs"
                      onClick={() => moveDown(idx)}
                      disabled={idx === fields.length - 1}
                    >
                      ↓
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="p-4 border-t flex justify-end gap-2">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "保存中..." : "保存"}
          </button>
        </div>
      </form>
    </div>
  );
}
